/**
 * Proposal Analytics — visitor intelligence dashboard for a single proposal.
 *
 * Fetches `/api/proposals/{id}/analytics` and renders:
 *   • 4 summary cards (Active now · Returning visitors · Multiple visits · Last activity)
 *   • Behavioural cards (Total engagement time · Most viewed section · Section breakdown)
 *   • AI/rule-based insights with coloured tones
 *   • Per-session timeline with geo, ISP, dwell, max scroll, sections, returning badge
 */
import React, { useEffect, useState } from 'react';
import {
    Eye, Clock, MapPin, Globe, ChevronDown, ChevronUp,
    Activity, RotateCcw, TrendingUp, FileText, Share2, Users,
    BarChart3, Layers, AlertTriangle,
} from 'lucide-react';
import api from '../../api';

const ICONS = {
    Activity, RotateCcw, TrendingUp, FileText, Share2, Eye, Clock, Layers,
};

const TONES = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    amber:   'bg-amber-50 border-amber-200 text-amber-900',
    red:     'bg-red-50 border-red-200 text-red-900',
    sky:     'bg-sky-50 border-sky-200 text-sky-900',
    violet:  'bg-violet-50 border-violet-200 text-violet-900',
    slate:   'bg-slate-50 border-slate-200 text-slate-900',
};

const SECTION_LABELS = {
    pricing:    'Pricing',
    terms:      'Terms & Conditions',
    narrative:  'Narrative',
    acceptance: 'Acceptance form',
    summary:    'Summary',
    page:       'Page',
};

const SECTION_TONES = {
    pricing:    'bg-red-50 text-red-700 border-red-200',
    terms:      'bg-sky-50 text-sky-700 border-sky-200',
    narrative:  'bg-emerald-50 text-emerald-700 border-emerald-200',
    acceptance: 'bg-violet-50 text-violet-700 border-violet-200',
    summary:    'bg-slate-50 text-slate-700 border-slate-200',
    page:       'bg-slate-50 text-slate-700 border-slate-200',
};

function fmtDuration(seconds) {
    if (!seconds || seconds < 1) return '0s';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    if (m < 60) return s > 0 ? `${m}m ${s}s` : `${m}m`;
    const h = Math.floor(m / 60);
    return `${h}h ${m % 60}m`;
}

function fmtAgo(iso) {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        const mins = Math.round((Date.now() - d.getTime()) / 60000);
        if (mins < 1)  return 'just now';
        if (mins < 60) return `${mins}m ago`;
        if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`;
        return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return '—'; }
}

function flagFor(cc) {
    if (!cc || cc.length !== 2) return '';
    // unicode regional indicator
    const A = 0x1F1E6;
    return String.fromCodePoint(...[...cc.toUpperCase()].map(c => A + c.charCodeAt(0) - 65));
}

export default function ProposalAnalytics({ proposalId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        if (!proposalId) return undefined;
        let live = true;
        const load = () => api.get(`/proposals/${proposalId}/analytics`)
            .then(r => { if (live) setData(r.data); })
            .catch(() => {})
            .finally(() => { if (live) setLoading(false); });
        load();
        const interval = setInterval(load, 60000);  // refresh every minute
        return () => { live = false; clearInterval(interval); };
    }, [proposalId]);

    if (loading) {
        return <div className="text-[12.5px] text-[color:var(--bm-text-muted)]">Loading analytics…</div>;
    }
    if (!data || !data.summary) {
        return null;
    }

    const s = data.summary;
    const sessions = data.sessions || [];
    const insights = data.insights || [];
    const proposal = data.proposal || {};

    // Fix the negative TTFV bug: only show TTFV when sent_at < first_viewed_at
    let ttfvLabel = null;
    if (proposal.sent_at && proposal.first_viewed_at) {
        const ttfv = (new Date(proposal.first_viewed_at).getTime() - new Date(proposal.sent_at).getTime()) / 1000;
        if (ttfv >= 0 && ttfv < 60 * 60 * 24 * 365) {
            ttfvLabel = fmtDuration(ttfv);
        }
    }

    return (
        <div className="bg-white border border-[color:var(--bm-border)] rounded-xl p-5 mb-5" data-testid="proposal-analytics-card">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <BarChart3 size={16} className="text-[color:var(--bm-text-muted)]" />
                    <h3 className="font-display text-[15px] font-semibold">Visitor analytics</h3>
                </div>
                <span className="text-[11px] text-[color:var(--bm-text-subtle)]">Auto-refreshes every minute</span>
            </div>

            {/* ── Top summary cards ─────────────────────────────────────── */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <KPI testId="kpi-active-now" tone={s.active_now > 0 ? 'emerald' : 'slate'} icon={<Activity size={14} />}
                    label="Active now" value={s.active_now}
                    hint={s.active_now > 0 ? 'Activity in last 5 min' : 'No one viewing right now'} />
                <KPI testId="kpi-returning" tone={s.returning_visitor_count > 0 ? 'amber' : 'slate'} icon={<RotateCcw size={14} />}
                    label="Returning visitors" value={s.returning_visitor_count}
                    hint="Distinct visitors who came back in a new session" />
                <KPI testId="kpi-views" tone="slate" icon={<Eye size={14} />}
                    label="Total views" value={s.total_views}
                    hint={`Across ${s.total_sessions} session${s.total_sessions === 1 ? '' : 's'}`} />
                <KPI testId="kpi-last-activity" tone="slate" icon={<Clock size={14} />}
                    label="Last activity" value={fmtAgo(s.last_activity)}
                    hint={proposal.last_viewed_at ? new Date(proposal.last_viewed_at).toLocaleString('en-GB') : '—'} />
            </div>

            {/* ── Behavioural cards (Phase 2) ───────────────────────────── */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <KPI testId="kpi-engagement" tone={s.total_engagement_seconds > 0 ? 'sky' : 'slate'}
                    icon={<Clock size={14} />}
                    label="Total engagement"
                    value={s.total_engagement_seconds > 0
                        ? `${s.dwell_includes_estimate ? '≥ ' : ''}${fmtDuration(s.total_engagement_seconds)}`
                        : '—'}
                    hint={s.dwell_includes_estimate
                        ? 'Lower-bound estimate from view-history (new visitors are tracked precisely via heartbeats)'
                        : 'Cumulative dwell time across all visitors'} />
                <KPI testId="kpi-most-viewed" tone={s.most_viewed_section ? 'violet' : 'slate'}
                    icon={<Layers size={14} />}
                    label="Most viewed"
                    value={s.most_viewed_section ? (SECTION_LABELS[s.most_viewed_section] || s.most_viewed_section) : '—'}
                    hint={s.most_viewed_section ? `${s.section_visit_totals?.[s.most_viewed_section] || 0} visits` : 'No section data yet'} />
                <KPI testId="kpi-internal-sharing" tone={s.internal_sharing ? 'violet' : 'slate'}
                    icon={<Share2 size={14} />}
                    label="Internal sharing"
                    value={s.internal_sharing ? 'Detected' : 'None'}
                    hint={s.internal_sharing
                        ? `${s.internal_sharing_groups?.[0]?.ip_count || 0} IPs on same device fingerprint`
                        : '<3 IPs share a device — looks like 1 visitor'} />
                <KPI testId="kpi-ttfv" tone="slate" icon={<Clock size={14} />}
                    label="Time to first view"
                    value={ttfvLabel || '—'}
                    hint={ttfvLabel ? 'Time between sending and first open' : 'No "sent" timestamp yet'} />
            </div>

            {/* ── Insights ──────────────────────────────────────────────── */}
            {insights.length > 0 && (
                <div className="space-y-2 mb-4" data-testid="proposal-insights">
                    {insights.map((ins, i) => {
                        const Icon = ICONS[ins.icon] || Activity;
                        return (
                            <div key={i} className={`flex items-start gap-3 rounded-md border px-3 py-2.5 text-[12.5px] ${TONES[ins.tone] || TONES.slate}`}
                                data-testid={`insight-${ins.kind}`}>
                                <Icon size={15} className="mt-0.5 shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold">{ins.title}</div>
                                    <div className="opacity-90 mt-0.5 leading-relaxed">{ins.detail}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* ── Section breakdown ─────────────────────────────────────── */}
            {Object.keys(s.section_visit_totals || {}).length > 0 && (
                <div className="mb-4" data-testid="section-breakdown">
                    <div className="text-[11.5px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-semibold mb-1.5">
                        Section engagement
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        {Object.entries(s.section_visit_totals)
                            .sort((a, b) => b[1] - a[1])
                            .map(([sec, count]) => (
                                <span key={sec}
                                    className={`inline-flex items-center gap-1.5 text-[11.5px] px-2 py-1 rounded-md border ${SECTION_TONES[sec] || SECTION_TONES.page}`}>
                                    {SECTION_LABELS[sec] || sec}
                                    <span className="font-semibold">{count}</span>
                                </span>
                            ))}
                    </div>
                </div>
            )}

            {/* ── Session timeline ──────────────────────────────────────── */}
            {sessions.length > 0 && (
                <div>
                    <button
                        type="button"
                        onClick={() => setExpanded(!expanded)}
                        className="flex items-center gap-1.5 text-[12px] font-medium text-[color:var(--bm-text-muted)] hover:text-[color:var(--bm-text)]"
                        data-testid="toggle-sessions-btn"
                    >
                        {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        {expanded ? 'Hide' : 'Show'} session history ({sessions.length})
                    </button>

                    {expanded && (
                        <div className="mt-3 space-y-2" data-testid="sessions-list">
                            {sessions.map(sess => (
                                <SessionRow key={sess.session_id} session={sess} />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {sessions.length === 0 && (
                <div className="text-center text-[12.5px] text-[color:var(--bm-text-muted)] py-4 border-t border-[color:var(--bm-border)] mt-2">
                    No tracked sessions yet. Once the client opens the public link, sessions appear here.
                </div>
            )}
        </div>
    );
}

function KPI({ tone, icon, label, value, hint, testId }) {
    return (
        <div className={`rounded-lg border px-3 py-2.5 ${TONES[tone] || TONES.slate}`} data-testid={testId}>
            <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wide font-semibold opacity-75">
                {icon}
                <span>{label}</span>
            </div>
            <div className="text-[18px] font-semibold mt-1 truncate">{value}</div>
            {hint && <div className="text-[10.5px] opacity-70 mt-0.5 line-clamp-2 leading-snug">{hint}</div>}
        </div>
    );
}

function SessionRow({ session }) {
    const geo = session.geo || {};
    const geoLabel = [geo.city, geo.country].filter(Boolean).join(', ');
    const ua = session.ua || '';
    const browser = (ua.match(/Edg|Chrome|Firefox|Safari|curl/i) || ['Browser'])[0];
    const os = (ua.match(/Windows|Mac OS X|Macintosh|Android|iPhone|iPad|Linux/i) || [])[0] || '';

    return (
        <div className="rounded-md border border-[color:var(--bm-border)] p-2.5 text-[12px] hover:bg-[color:var(--bm-bg-mute)]/30 transition" data-testid={`session-${session.session_id}`}>
            <div className="flex items-start gap-3">
                {/* Indicator dot */}
                <div className={`w-1.5 mt-2 rounded-full self-stretch ${session.is_active ? 'bg-emerald-500' : session.is_returning ? 'bg-amber-400' : 'bg-slate-300'}`} />
                <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-12 gap-2 items-start">
                    {/* Time + duration */}
                    <div className="md:col-span-3">
                        <div className="font-medium text-[12.5px]">{fmtAgo(session.last_at)}</div>
                        <div className="text-[10.5px] text-[color:var(--bm-text-subtle)] truncate">
                            {new Date(session.started_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            {session.engagement_seconds > 0 && (
                                <> · <Clock size={9} className="inline" /> <span
                                    title={session.dwell_is_estimate ? 'Lower-bound estimate (multiple page-loads in the same session)' : 'Measured from heartbeats'}>
                                    {session.dwell_is_estimate ? '≥ ' : ''}{fmtDuration(session.engagement_seconds)}
                                </span></>
                            )}
                        </div>
                    </div>
                    {/* Geo */}
                    <div className="md:col-span-3">
                        <div className="flex items-center gap-1.5 text-[12px]">
                            {geo.country_code && <span aria-hidden="true">{flagFor(geo.country_code)}</span>}
                            <MapPin size={11} className="text-[color:var(--bm-text-muted)] shrink-0" />
                            <span className="truncate">{geoLabel || session.ip_truncated || session.ip || '—'}</span>
                        </div>
                        {geo.org && (
                            <div className="flex items-center gap-1 text-[10.5px] text-[color:var(--bm-text-subtle)] truncate">
                                <Globe size={9} /> <span className="truncate">{geo.org}</span>
                            </div>
                        )}
                    </div>
                    {/* Device */}
                    <div className="md:col-span-3">
                        <div className="text-[12px]">{browser} · {os || 'Unknown OS'}</div>
                        <div className="text-[10.5px] text-[color:var(--bm-text-subtle)]">
                            {session.view_count} view{session.view_count === 1 ? '' : 's'}
                            {session.max_scroll > 0 && <> · {session.max_scroll}% scroll</>}
                        </div>
                    </div>
                    {/* Badges */}
                    <div className="md:col-span-3 flex flex-wrap gap-1 items-start justify-end">
                        {session.is_active && (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">● Live</span>
                        )}
                        {session.is_returning && (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Returning</span>
                        )}
                        {Object.entries(session.sections || {}).map(([sec, n]) => (
                            <span key={sec} className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${SECTION_TONES[sec] || SECTION_TONES.page}`}>
                                {SECTION_LABELS[sec] || sec} {n > 1 && <span className="font-semibold">·{n}</span>}
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
